<script>
import Layout from '@layouts/adminLayout.vue'
import { getAllApps } from '@src/api/apps'
import { getFavoriteResources } from '@src/api/resources'
import { getAllEvents } from '@src/api/events'
import { transformMany as transformApps } from '@src/api/transformers/appTransformer'
import { transformMany as transformResources } from '@src/api/transformers/resourceTransformer.js'
import { transformMany as transformEvents } from '@src/api/transformers/eventTransformer.js'
import ResourceBlock from '@components/resources/resource-block.vue'
import ModalResource from '@components/resources/modal-resource.vue'
import ModalEvent from '@components/events/modal-event.vue'
import MottoCalendar from '@components/events/motto-calendar.vue'
import EventBlock from '@components/events/event-block.vue'
import SelectEventType from '@components/events/select-event-type.vue'
import ModalOptInMobilePrompt from '@components/modal-opt-in-mobile-prompt.vue'
import { mottoOptInMobileMixin } from '@src/mixins/mottoOptInMobile'
import { appsComputed, authComputed, eventsComputed, resourcesComputed } from '@state/helpers'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import _ from 'lodash'
import moment from 'moment'
import { hasReceivedExpiringReferrals } from '@src/api/networkConnect'
import SkeletonLoader from '@components/skeleton-loader.vue'
import AppBlockSkeleton from '@components/app-block-skeleton.vue'
import BannerText from '@components/banner-text.vue'
import AppBlockContainerWithTag from '@components/apps/app-block-container-with-tag.vue'
import { getDescriptionsForNewTailoredTechApps, isAppNewAccess } from '@utils/consts/technologyIcons'
import ModalAppNewAccess from '@components/resources/modal-app-new-access.vue'

export default {
  page: {
    title: 'Home',
    meta: [
      {
        name: 'description',
        content: 'The Home page.',
      },
    ],
  },
  components: {
    ModalAppNewAccess,
    AppBlockContainerWithTag,
    Layout,
    SkeletonLoader,
    AppBlockSkeleton,
    MottoCalendar,
    EventBlock,
    SelectEventType,
    ResourceBlock,
    ModalResource,
    ModalEvent,
    ModalOptInMobilePrompt,
    BannerText,
  },
  mixins: [isMobileMixin, mottoOptInMobileMixin],
  data() {
    return {
      loading: true,
      error: false,
      perPageAppCarousel: [[0, 4]],
      perPageResourceCarousel: [[0, 3]],
      perPageEventCarousel: [[0, 3]],
      selectedDate: null,
      selectedMeetingType: null,
      selectedAudience: null,
      selectedEventType: [],
    }
  },
  computed: {
    ...appsComputed,
    ...resourcesComputed,
    ...eventsComputed,
    ...authComputed,
    getUpcomingEvents() {
      const amountToShow = 3
      const upcomingEvents = this.applySelectedFilters(this.upcomingEvents)
      return _.take(upcomingEvents, amountToShow)
    },
    getSelectedDateFormatted() {
      if (!this.selectedDate) {
        return
      }

      const nth = function (d) {
        if (d > 3 && d < 21) return 'th'
        switch (d % 10) {
          case 1:
            return 'st'
          case 2:
            return 'nd'
          case 3:
            return 'rd'
          default:
            return 'th'
        }
      }
      const date = moment(this.selectedDate).toDate()
      const day = date.toLocaleString('en-US', { day: 'numeric' })
      const month = date.toLocaleString('en-US', { month: 'long' })

      return `${month} ${day}${nth(day)}`
    },
    getEventsOnSelectedDate() {
      if (!this.selectedDate) {
        return
      }

      const selectedDateObject = moment(this.selectedDate).toDate()

      return this.applySelectedFilters(this.getEventsOnDate(selectedDateObject))
    },
    getAllEventsFiltered() {
      return this.applySelectedFilters(this.allEvents)
    },
    getFavoriteEventsFiltered() {
      return this.applySelectedFilters(this.favoriteEvents)
    },
    getFavoriteEventsOrdered() {
      return _.orderBy(this.favoriteEvents, ({ start }) => moment(start, 'YYYY-MM-DD hh:mm:ss').toDate())
    },
    getAudienceOptions() {
      const audiences = ['Loan Originators and LOAs', 'Processors', 'Broker Owners / Operations Managers']

      if (this.isMottoCorporate) {
        return [...audiences, 'Internal']
      }
      return audiences
    },
    newAppsDescriptions() {
      return this.allApps
        .filter((app) => isAppNewAccess(app.appName, this.currentUser))
        .map((app) => {
          return getDescriptionsForNewTailoredTechApps(app.appName, this.currentUser, {
            onlyNewGroups: true,
            addNewAsterisk: false,
          })
        })
    },
  },
  created() {
    this.fetchHomeData()
  },
  updated() {
    this.recalculateAllCarousels()
  },
  mounted() {
    document.addEventListener('click', this.handlePageClick)
    this.checkReferralExpireReminder()
    this.checkPersonalEmailChanged()
    if (this.currentUser?.shouldPromptOptInToMarketingTexts && this.hasSmsPromptsFeature) {
      this.openHomeOptInMobilePromptModal()
    }
    if (this.$route.query?.prompt === 'smsprompt') {
      this.openHomeOptInMobilePromptModal()
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handlePageClick)
  },
  methods: {
    async fetchHomeData() {
      try {
        const [
          {
            data: { data: allApps },
          },
          {
            data: { data: favoriteResources },
          },
          {
            data: { data: allEvents },
          },
        ] = await Promise.all([getAllApps(), getFavoriteResources(), getAllEvents()])
        this.$store.commit(
          'apps/SET_APPS',
          transformApps(
            allApps.map((app) => ({
              ...app,
              isNewApp: isAppNewAccess(app.customTitle ? app.customTitle : app.title, this.currentUser),
            }))
          )
        )

        this.$store.commit('events/SET_EVENTS', transformEvents(allEvents))
        this.$store.commit(
          'resources/SET_RESOURCES',
          transformResources(
            favoriteResources.map((favoriteResource) => ({
              ...favoriteResource,
              isFavorite: true,
            }))
          )
        )
        this.$store.commit('resources/SET_SHOULD_RELOAD_RESOURCES', true)
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    checkPersonalEmailChanged() {
      if (!this.shouldShowPersonalEmailChangedToast) {
        return
      }
      if (!this.currentUser) {
        return
      }
      if (!this.currentUser.secondaryEmailChangedAt) {
        return
      }
      const changedAt = this.currentUser.secondaryEmailChangedAt.substr(0, 10)
      const today = new Date().toISOString().substr(0, 10)
      let message = null
      if (this.currentUser.secondaryEmailTemp && changedAt !== today) {
        message =
          'It looks like you tried to update your Personal Email address. Please open the confirmation email we sent to you and click the link to complete this change.'
      }
      if (!this.currentUser.secondaryEmailTemp) {
        message =
          'We could not verify your email address, and so this change has been reverted. Please email help@mottomortgage.com or call 866-MOTTO60 for assistance.'
      }
      if (!message) {
        return
      }
      this.$bvToast.toast(message, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-primary-blue',
        noAutoHide: true,
        id: 'toast-personal-email-changed',
      })
      this.$root.$on('bv::toast::hidden', (event) => {
        if (event.componentId === 'toast-personal-email-changed') {
          this.$store.commit('auth/SET_SHOW_PERSONAL_EMAIL_CHANGED_TOAST', false)
        }
      })
    },
    async checkReferralExpireReminder() {
      if (!this.shouldShowReferralExpiredReminderToast) {
        return
      }
      if (!this.currentUser) {
        return
      }

      const {
        data: { response: showMessageExpiringReferrals },
      } = await hasReceivedExpiringReferrals()

      if (!showMessageExpiringReferrals) {
        this.$store.commit('auth/SET_SHOW_REFERRAL_EXPIRED_REMINDER_TOAST', false)
        return
      }

      const message =
        'You have an assigned referral that will expire in the next 24 hours. Click here to access Network Connect.'
      this.$bvToast.toast(message, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-primary-blue',
        noAutoHide: true,
        to: 'network-connect',
        id: 'toast-referral-expire-reminder',
      })
      this.$root.$on('bv::toast::hidden', (event) => {
        if (event.componentId === 'toast-referral-expire-reminder') {
          this.$store.commit('auth/SET_SHOW_REFERRAL_EXPIRED_REMINDER_TOAST', false)
        }
      })
    },
    recalculateAllCarousels() {
      this.recalculatePerPageAppsCarousel()
      this.recalculatePerPageResourcesCarousel()
      this.recalculatePerPageEventCarousel()
    },
    recalculatePerPageAppsCarousel() {
      const appCarousel = this.$refs.appsCarousel

      if (!appCarousel) {
        return
      }
      const appCarouselWidth = appCarousel.carouselWidth
      const appBlockWidth = 98
      const appBlockMargin = 24
      const perPageAppCarousel = [[0, Math.floor(appCarouselWidth / (appBlockWidth + appBlockMargin))]]
      if (!_.isEqual(this.perPageAppCarousel, perPageAppCarousel)) {
        this.perPageAppCarousel = perPageAppCarousel
      }
    },
    recalculatePerPageResourcesCarousel() {
      const resourceCarousel = this.$refs.resourcesCarousel

      if (!resourceCarousel) {
        return
      }
      const resourceCarouselWidth = resourceCarousel.carouselWidth
      const resourceCarouselBlockWidth = 150
      const resourceCarouselBlockMargin = 16
      const perPageResourceCarousel = [
        [0, Math.floor(resourceCarouselWidth / (resourceCarouselBlockWidth + resourceCarouselBlockMargin))],
      ]
      if (!_.isEqual(this.perPageResourceCarousel, perPageResourceCarousel)) {
        this.perPageResourceCarousel = perPageResourceCarousel
      }
    },
    recalculatePerPageEventCarousel() {
      const eventCarousel = this.$refs.eventCarousel

      if (!eventCarousel) {
        return
      }
      const eventCarouselWidth = eventCarousel.carouselWidth
      const eventCarouselBlockWidth = 127
      const eventCarouselBlockMargin = 16
      const perPageEventCarousel = [
        [0, Math.floor(eventCarouselWidth / (eventCarouselBlockWidth + eventCarouselBlockMargin))],
      ]
      if (!_.isEqual(this.perPageEventCarousel, perPageEventCarousel)) {
        this.perPageEventCarousel = perPageEventCarousel
      }
    },
    selectDate(newDate) {
      this.selectedDate = newDate
    },
    clearDate() {
      this.selectedDate = null
    },
    handlePageClick(evt) {
      if (this.shouldClearSelectedDate(evt)) {
        this.clearDate()
      }
    },
    shouldClearSelectedDate(clickEvent) {
      const elemTree = clickEvent.composedPath()

      const hasPointerCursor = window.getComputedStyle(clickEvent.target).cursor === 'pointer'

      if (hasPointerCursor) {
        return false
      }

      const isInsideEventsColumn = elemTree.some((node) => {
        try {
          return node?.getAttributeNames().includes('data-event-column')
        } catch (err) {
          return false
        }
      })

      if (isInsideEventsColumn) {
        return false
      }

      const isInsideModal = elemTree.some((node) => {
        try {
          return node?.getAttribute('id') === 'view-resource-modal' || node?.getAttribute('id') === 'view-event-modal'
        } catch (err) {
          return false
        }
      })

      return !isInsideModal
    },
    applySelectedFilters: function (events) {
      return _.filter(events, (event) => {
        if (this.selectedMeetingType && event.meetingType !== this.selectedMeetingType) {
          return false
        }

        if (this.selectedAudience && !event.audience.includes(this.selectedAudience)) {
          return false
        }

        if (
          this.selectedEventType &&
          this.selectedEventType.length &&
          !this.selectedEventType.includes(event.eventType)
        ) {
          return false
        }

        return true
      })
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.viewContainer">
      <div>
        <BannerText />
        <BaseTitle>Home</BaseTitle>
        <div :class="$style.homeContainer">
          <div :class="$style.favoriteColumn">
            <div :class="$style.carouselWrapper">
              <h3>Favorite Apps</h3>
              <p v-if="!favoriteApps.length && !loading" class="color-dark-gray text-center">
                Go to Apps and click the heart to favorite an app.
              </p>
              <div v-if="loading">
                <div class="d-flex" :class="$style.appBlockContainer">
                  <span class="w-100">
                    <AppBlockSkeleton is-mobile has-card />
                    <AppBlockSkeleton is-mobile has-card />
                    <AppBlockSkeleton is-mobile has-card />
                    <AppBlockSkeleton is-mobile has-card />
                    <AppBlockSkeleton is-mobile has-card />
                  </span>
                </div>
              </div>
              <div v-else :class="$style.appBlockContainer">
                <transition-group>
                  <div v-for="app in favoriteApps" :key="app.appId">
                    <AppBlockContainerWithTag :app="app" />
                  </div>
                </transition-group>
              </div>
            </div>
            <div :class="$style.carouselWrapper">
              <h3>Favorite Resources</h3>
              <p v-if="!favoriteResources.length && !loading" class="color-dark-gray text-center">
                Go to Resources and click the heart to favorite a resource.
              </p>
              <div v-if="loading" style="display: flex; gap: 50px; overflow: hidden">
                <SkeletonLoader style="flex-shrink: 0; width: 150px; height: 193px" />
                <SkeletonLoader style="flex-shrink: 0; width: 150px; height: 193px" />
                <SkeletonLoader style="flex-shrink: 0; width: 150px; height: 193px" />
              </div>
              <carousel
                v-if="favoriteResources.length && !isMobile && !loading"
                ref="resourcesCarousel"
                :per-page-custom="perPageResourceCarousel"
                :pagination-enabled="false"
                :navigation-enabled="true"
                :navigation-next-label="''"
                :navigation-prev-label="''"
                :class="$style.carousel">
                <slide v-for="resource in favoriteResources" :key="resource.resourceId">
                  <div :class="$style.resourceBlockContainer">
                    <ResourceBlock :resource="resource" :minimal-design="true" />
                  </div>
                </slide>
              </carousel>
              <div v-if="favoriteResources.length && isMobile && !loading" :class="$style.mobileCarousel">
                <div
                  v-for="resource in favoriteResources"
                  :key="resource.resourceId"
                  :class="$style.resourceBlockContainer">
                  <ResourceBlock :resource="resource" :minimal-design="true" />
                </div>
              </div>
            </div>
            <div :class="$style.carouselWrapper">
              <h3>Favorite Events</h3>
              <p v-if="!favoriteEvents.length && !loading" class="color-dark-gray text-center">
                Select a calendar day to view Events, then click the flag to favorite an event.
              </p>
              <div v-if="loading" style="display: flex; gap: 50px; overflow: hidden">
                <SkeletonLoader style="flex-shrink: 0; width: 150px; height: 193px" />
                <SkeletonLoader style="flex-shrink: 0; width: 150px; height: 193px" />
                <SkeletonLoader style="flex-shrink: 0; width: 150px; height: 193px" />
              </div>

              <carousel
                v-if="favoriteEvents.length && !isMobile && !loading"
                ref="eventCarousel"
                :per-page-custom="perPageEventCarousel"
                :pagination-enabled="false"
                :navigation-enabled="true"
                :navigation-next-label="''"
                :navigation-prev-label="''"
                :class="$style.carousel">
                <slide v-for="event in getFavoriteEventsOrdered" :key="event.eventId">
                  <div :class="$style.eventBlockContainerInCarousel">
                    <EventBlock :key="event.eventId" :event="event" :minimal-design="true" />
                  </div>
                </slide>
              </carousel>
              <div v-if="favoriteEvents.length && isMobile" :class="$style.mobileCarousel">
                <div
                  v-for="event in getFavoriteEventsOrdered"
                  :key="event.eventId"
                  :class="$style.eventBlockContainerInCarousel">
                  <EventBlock :key="event.eventId" :event="event" :minimal-design="true" />
                </div>
              </div>
            </div>
          </div>
          <div :class="$style.eventsColumn" data-event-column>
            <h3 class="m-0">Events</h3>
            <div class="mt-3 d-flex justify-content-around">
              <BaseSelect
                v-model="selectedMeetingType"
                :class="$style.eventBaseSelect"
                :disabled="loading"
                empty-text="All Meeting Types"
                name="MeetingType"
                :options="['Live', 'Virtual', 'Hybrid']" />
              <BaseSelect
                v-model="selectedAudience"
                :class="$style.eventBaseSelect"
                :disabled="loading"
                empty-text="All Audiences"
                name="AudienceType"
                :options="getAudienceOptions" />
            </div>
            <div class="mt-3 d-flex justify-content-around">
              <SelectEventType :selected-event-type-prop.sync="selectedEventType" :disabled="loading" />
            </div>
            <div class="mt-3 mb-3" :class="$style.divider"></div>
            <div v-if="loading">
              <SkeletonLoader
                :style="{
                  height: '38px',
                  width: '100%',
                }" />
              <SkeletonLoader
                :style="{
                  height: '280px',
                  width: '100%',
                  'margin-top': '20px',
                }" />
            </div>
            <MottoCalendar
              v-else
              :events="getAllEventsFiltered"
              :class="$style.calendar"
              :selected-date-prop.sync="selectedDate"
              @updateDate="selectDate" />
            <div class="mt-3 mb-4" :class="$style.divider"></div>
            <template v-if="!selectedDate">
              <h3>Upcoming Events</h3>
              <div v-if="loading">
                <SkeletonLoader style="width: 100%; height: 160px; margin-top: 10px; margin-bottom: 10px" />
                <SkeletonLoader style="width: 100%; height: 193px; margin-bottom: 10px" />
                <SkeletonLoader style="width: 100%; height: 193px; margin-bottom: 10px" />
              </div>
              <div v-else :class="$style.eventBlockContainer">
                <EventBlock
                  v-for="event in getUpcomingEvents"
                  :key="event.eventId"
                  :current-date="selectedDate ? selectedDate : new Date()"
                  :event="event" />
                <p v-if="!getUpcomingEvents.length" class="text-center tag-text">No upcoming events</p>
              </div>
            </template>
            <template v-if="selectedDate">
              <h3>Events on {{ getSelectedDateFormatted }}</h3>
              <div :class="$style.eventBlockContainer">
                <EventBlock
                  v-for="event in getEventsOnSelectedDate"
                  :key="event.eventId"
                  :current-date="selectedDate ? selectedDate : new Date()"
                  :event="event" />
                <p v-if="!getEventsOnSelectedDate.length" class="text-center tag-text">No events on this date</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <b-modal
      id="view-resource-modal"
      :modal-class="['right', 'modal-resource']"
      hide-footer
      hide-header
      title="BootstrapVue">
      <ModalResource :toast-component="this.$bvToast" />
    </b-modal>
    <b-modal
      id="view-event-modal"
      :modal-class="['right', 'modal-event']"
      hide-footer
      hide-header
      title="BootstrapVueEvent">
      <ModalEvent :toast-component="this.$bvToast" />
    </b-modal>
    <ModalOptInMobilePrompt :mobile-number="currentUser.phoneMobile"></ModalOptInMobilePrompt>
    <ModalAppNewAccess
      :app-descriptions="newAppsDescriptions"
      :selected-app-name-description="$store.getters['apps/selectedNewAccessApp']" />
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.homeContainer {
  display: flex;
  flex-wrap: wrap;
  margin-top: 32px;
}

.favoriteColumn {
  @media (max-width: $lg-breakpoint) {
    width: auto;
    min-width: 100%;
  }

  flex: 9999 1 400px;
  width: 600px;
  min-width: 400px;
}

.appBlockContainer {
  @media (max-width: $lg-breakpoint) {
    margin-right: 24px;
  }

  margin: 0 auto;

  > span {
    @media (max-width: 1400px) {
      grid-gap: 32px 12px;
    }

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(98px, 1fr));
    grid-gap: 32px 16px;
    margin-top: 0;
  }
}

.appBlock {
  margin-bottom: 8px;
}

.appTag {
  width: 98px;
}

.eventsColumn {
  @media (max-width: $lg-breakpoint) {
    min-width: 100%;
    padding: 0;
    margin-top: 0;
    border-color: unset;
    border-radius: unset;
    box-shadow: unset;
  }

  flex: 1 1 370px;
  width: 370px;
  padding: 20px;
  margin-top: 20px;
  border-color: $lightest-gray !important;
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(196, 196, 196, 1);
}

.carouselWrapper {
  @media (max-width: $lg-breakpoint) {
    width: auto;
  }

  width: 90%;
  margin-bottom: 48px;

  h3 {
    margin-bottom: 24px;
  }
}

.carousel {
  @media (max-width: $lg-breakpoint) {
    width: 85%;
    margin: 0 auto;
  }

  > div {
    padding-bottom: 7px;
  }
}

.mobileCarousel {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    padding-bottom: 10px;
    overflow-x: scroll;
  }

  display: none;
}

.eventBlockContainer {
  @media (max-width: $lg-breakpoint) {
    padding: 5px;
    margin-bottom: 20px;
  }

  > * {
    margin-top: 20px;
  }
}

.eventBlockContainerInCarousel {
  @media (max-width: $lg-breakpoint) {
    height: auto;
    margin-right: 16px;
  }

  height: 100%;

  > * {
    width: 127px;
    height: 100%;
    margin: 0 auto;
  }
}

.resourceBlockContainer {
  @media (max-width: $lg-breakpoint) {
    height: auto;
    margin-right: 16px;
  }

  height: 100%;

  > * {
    width: 150px;
    margin: 0 auto;
  }
}

.eventBaseSelect {
  @media (max-width: $lg-breakpoint) {
    flex: 1 0 auto;
    width: min-content;
    margin-right: 10px;

    select {
      width: 100% !important;
    }
  }

  flex: 0 0 150px;

  select {
    width: 150px;
    height: 30px;
    padding: 5px 20px 5px 16px;
    font-size: 14px;
  }

  svg {
    top: 9px;
    right: 8px;
    width: 0.7em !important;
  }
}

.divider {
  height: 1px;
  background: $light-gray;
  background: -webkit-gradient(
    linear,
    0 0,
    100% 0,
    from($lightest-gray),
    to($lightest-gray),
    color-stop(50%, $light-gray)
  );
  border: 0;
}
</style>
